import React, { useState } from 'react';
import { useTranslation } from '@/store/languageStore';
import type { ProjectMilestone, ProjectSource } from '@/types/projects';
import { projectApi } from '@/services/projects/projectApi';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

import DateInput from '@/components/common/DateInput';

interface Props {
  source: ProjectSource;
  projectId: number;
  milestones: ProjectMilestone[];
  onRefresh: () => void;
}

export const MilestonePanel: React.FC<Props> = ({ source, projectId, milestones, onRefresh }) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [adding, setAdding] = useState(false);

  const sortedMilestones = [...milestones].sort((a, b) => {
    const dateA = (a.due_date || a.target_date || '').slice(0, 10);
    const dateB = (b.due_date || b.target_date || '').slice(0, 10);
    if (!dateA && !dateB) return a.id - b.id;
    if (!dateA) return 1;
    if (!dateB) return -1;
    if (dateA !== dateB) return dateA.localeCompare(dateB);
    return a.id - b.id;
  });

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setAdding(true);
    try {
      const payload = source === 'crm'
        ? { name, due_date: dueDate || undefined }
        : { name, target_date: dueDate || undefined };
      await projectApi.createMilestone(source, projectId, payload);
      setName('');
      setDueDate('');
      onRefresh();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : t('common.error');
      toast.error(message);
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm(t('common.confirmDelete'))) return;
    await projectApi.deleteMilestone(source, id);
    onRefresh();
  };

  return (
    <div className="space-y-3">
      <h3 className="font-semibold text-textDefault">{t('projects.milestones')}</h3>
      <ul className="space-y-2">
        {sortedMilestones.map((m) => (
          <li key={m.id} className="flex items-center justify-between p-3 bg-bgElevated rounded-lg border border-border">
            <div>
              <p className="font-medium text-textDefault">{m.name}</p>
              <p className="text-xs text-textSecondary">
                {(m.due_date || m.target_date)?.slice(0, 10) || '—'}
                {m.release_version && ` · ${m.release_version}`}
              </p>
            </div>
            <button type="button" onClick={() => handleDelete(m.id)} className="p-1 text-red-500 hover:bg-red-50 rounded">
              <TrashIcon className="w-4 h-4" />
            </button>
          </li>
        ))}
      </ul>
      <form onSubmit={handleAdd} className="flex gap-2 flex-wrap">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('projects.milestoneName')}
          className="flex-1 min-w-[120px] px-3 py-2 border border-border rounded-lg bg-bgBase text-textDefault text-sm"
        />
        <DateInput value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
          className="px-3 py-2 border border-border rounded-lg bg-bgBase text-textDefault text-sm"
        />
        <button
          type="submit"
          disabled={adding}
          className="flex items-center gap-1 px-3 py-2 bg-primary text-white rounded-lg text-sm disabled:opacity-50"
        >
          <PlusIcon className="w-4 h-4" />
          {t('common.add')}
        </button>
      </form>
    </div>
  );
};
